import cx from 'classnames'
import 'keen-slider/keen-slider.min.css'
import { KeenSliderOptions, useKeenSlider } from 'keen-slider/react'
import React, {
  cloneElement,
  FC,
  ReactElement,
  useCallback,
  useState,
} from 'react'
import { sizes } from 'src/theme'

import { Icons } from '../Icon'

import * as SC from './styled'

export type SliderProps = {
  className?: string
  slides: {
    node: React.ReactNode
    forcedRatio?: {
      ratio: number
      heightReference: number
      breakpoint?: keyof typeof sizes
    }
  }[]
  keenOptions?: KeenSliderOptions
  texts?: {
    next?: string
    previous?: string
    dot?: string
  }
  isForDesktop?: boolean // for the rare desktop slider
  role?: string
}

const Slider: FC<SliderProps> = ({
  className,
  slides,
  texts,
  keenOptions = {
    drag: true,
    slides: { perView: 1.5, spacing: 16 },
    defaultAnimation: {
      duration: 300,
    },
    breakpoints: {
      // 600 as in sizes
      '(min-width: 37.5em)': {
        slides: { perView: 2.15, spacing: 16 },
      },
      // 900 as in sizes
      '(min-width: 56.25em)': {
        disabled: true,
      },
    },
  },
  isForDesktop,
  role,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [sliderRef, instanceRef] = useKeenSlider({
    ...keenOptions,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel)
    },
    created() {
      setLoaded(true)
    },
  })

  // change slide
  const handleSlideChange = useCallback(
    (slide: number) => {
      if (loaded && instanceRef.current) {
        instanceRef.current?.moveToIdx(slide)
      }
    },
    [instanceRef, loaded]
  )

  return (
    <SC.Container className={cx('Slider', className)}>
      <SC.Slider className="keen-slider" ref={sliderRef} role={role}>
        {slides.map((slide, index) => (
          <SC.Slide
            key={index}
            className="keen-slider__slide"
            $forcedRatio={slide.forcedRatio ? true : false}
            $breakpoint={slide.forcedRatio?.breakpoint}
            $forcedRatioHeight={slide?.forcedRatio?.heightReference ?? 500}
            $forcedRatioWidth={
              (slide?.forcedRatio?.heightReference ?? 500) *
              (slide?.forcedRatio?.ratio ?? 1)
            }
          >
            {cloneElement(slide.node as ReactElement<any>, { index })}
          </SC.Slide>
        ))}
      </SC.Slider>
      <SC.Nav $visibleOnDesktop={isForDesktop}>
        <SC.Dots>
          {slides.map((_card, index) => (
            <SC.Dot
              key={index}
              onClick={() => handleSlideChange(index)}
              {...(index === currentSlide ? { 'aria-current': true } : {})}
            >
              <span>
                {texts?.dot} {index}
              </span>
            </SC.Dot>
          ))}
        </SC.Dots>
        <SC.Arrows>
          <SC.Arrow
            type="button"
            onClick={() => handleSlideChange(currentSlide - 1)}
            disabled={currentSlide === 0}
          >
            <SC.ArrowIcon icon={Icons.chevronLeft} title={texts?.previous} />
          </SC.Arrow>
          <SC.Numerotation>
            {(currentSlide + 1).toString().padStart(2, '0')} —{' '}
            {slides.length.toString().padStart(2, '0')}
          </SC.Numerotation>
          <SC.Arrow
            type="button"
            onClick={() => handleSlideChange(currentSlide + 1)}
            disabled={currentSlide === slides.length - 1}
          >
            <SC.ArrowIcon icon={Icons.chevronRight} title={texts?.next} />
          </SC.Arrow>
        </SC.Arrows>
      </SC.Nav>
    </SC.Container>
  )
}

Slider.displayName = 'Slider'

export default Slider
